// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// WB-001 / WB-012 — transport-agnostic drawing engine.
//
// `WhiteboardEngine` renders a `<canvas>` from an ordered op-log and turns local
// pointer input into ops. It knows NOTHING about React, the API client, RTC, or
// persistence. Locally-produced ops are surfaced via `onLocalOp` (a transport ships
// them); remote ops arrive through `applyRemote`. Coordinates are normalized to the
// unit square so the same op renders identically on canvases of different pixel sizes.
//
// WB-012 adds tools beyond the pen: highlighter, eraser, shapes (line/rect/ellipse/
// arrow), and — driven by the host via `onTextRequest` — text + sticky notes, plus
// per-author undo/redo.

import {
  type Op,
  type DrawOp,
  type StrokeOp,
  type ShapeOp,
  type TextOp,
  type StickyOp,
  type EraseOp,
  type ClearOp,
  type Point,
  type Size,
  clampUnit,
  isDrawOp,
  normToPixel,
  pixelToNorm,
} from "./types";

/** The active drawing tool. */
export type Tool = "pen" | "highlighter" | "eraser" | "line" | "rect" | "ellipse" | "arrow" | "text" | "sticky";

/** Options controlling a {@link WhiteboardEngine}. */
export interface EngineOptions {
  /** Identity stamped on locally-produced ops (e.g. the local user id). */
  author: string;
  /** Stroke color for local drawing (per-author color). Default `#e0e0e0`. */
  color?: string;
  /** Logical reference resolution; stroke width + text size scale relative to it. */
  referenceSize?: Size;
  /** Stroke width in reference units. Default `3`. */
  strokeWidth?: number;
  /**
   * If set (ms), strokes fade out and are removed this long after they're drawn — so
   * screen-share annotations don't pile up (Slack-style). Purely visual/local (no erase
   * op emitted; peers fade independently). `0`/undefined = persistent.
   */
  fadeMs?: number;
  /** Called for every op produced by LOCAL input. */
  onLocalOp?: (op: Op) => void;
  /**
   * The text/sticky tools can't capture text in a canvas, so on a click with one of
   * those tools active the engine asks the host to collect text at the given point; the
   * host then calls {@link WhiteboardEngine.addText}. WB-012 wires an inline editor.
   */
  onTextRequest?: (at: Point, kind: "text" | "sticky") => void;
  /** Override the op-id generator (tests inject a deterministic one). */
  idgen?: () => string;
  /** Override the clock (tests inject a deterministic one). */
  now?: () => number;
  /** Attach pointer listeners on construction. Default `true` (false = render-only mirror). */
  interactive?: boolean;
}

type Ctx = CanvasRenderingContext2D;

const DEFAULT_COLOR = "#e0e0e0";
const DEFAULT_REFERENCE: Size = { width: 1000, height: 1000 };
const DEFAULT_STROKE_WIDTH = 3;
const MIN_POINT_DISTANCE = 0.0025;
const MIN_POINT_INTERVAL_MS = 16;
const HIT_TOLERANCE = 0.02;
const FADE_DURATION_MS = 1200;
// Highlighter: wider + translucent.
const HIGHLIGHT_WIDTH_MULT = 5;
const HIGHLIGHT_ALPHA = 0.35;
// Text/sticky defaults (in reference units / normalized).
const DEFAULT_TEXT_SIZE = 28;
const STICKY_W = 0.2;
const STICKY_H = 0.14;

/**
 * Opacity of a fading stroke given its age: fully opaque until `hold` ms, then linearly
 * to 0 over `duration` ms. Pure (unit-tested); the engine removes an item once this hits 0.
 */
export function fadeOpacity(age: number, hold: number, duration = FADE_DURATION_MS): number {
  if (age <= hold) return 1;
  const t = (age - hold) / duration;
  return t >= 1 ? 0 : 1 - t;
}

// The active local gesture being drawn (pointer down → up).
type Gesture =
  | { kind: "stroke"; points: Point[]; lastTs: number; highlight: boolean }
  | { kind: "shape"; shape: "line" | "rect" | "ellipse" | "arrow"; from: Point; to: Point }
  | { kind: "erase" };

export class WhiteboardEngine {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: Ctx;
  private readonly author: string;
  private color: string;
  private readonly referenceSize: Size;
  private strokeWidth: number;
  private readonly fadeMs: number;
  private readonly onLocalOp?: (op: Op) => void;
  private readonly onTextRequest?: (at: Point, kind: "text" | "sticky") => void;
  private readonly now: () => number;
  private readonly nextId: () => string;

  private tool: Tool = "pen";
  /** Visible items in render order. */
  private items: DrawOp[] = [];
  // Local apply-time per item (drives fade opacity/removal) when fade is on.
  private readonly addedAt = new Map<string, number>();
  private fadeTimer: ReturnType<typeof setTimeout> | null = null;
  // Per-author redo stack: ops this client undid, newest last. Cleared on any new draw.
  private redoStack: DrawOp[] = [];

  private active: Gesture | null = null;
  private destroyed = false;
  private idCounter = 0;

  private readonly onDown = (e: PointerLike) => this.handlePointerDown(e);
  private readonly onMove = (e: PointerLike) => this.handlePointerMove(e);
  private readonly onUp = (e: PointerLike) => this.handlePointerUp(e);

  constructor(canvas: HTMLCanvasElement, opts: EngineOptions) {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("WhiteboardEngine: 2D canvas context unavailable");
    this.canvas = canvas;
    this.ctx = ctx;
    this.author = opts.author;
    this.color = opts.color ?? DEFAULT_COLOR;
    this.referenceSize = opts.referenceSize ?? DEFAULT_REFERENCE;
    this.strokeWidth = opts.strokeWidth ?? DEFAULT_STROKE_WIDTH;
    this.fadeMs = opts.fadeMs ?? 0;
    this.onLocalOp = opts.onLocalOp;
    this.onTextRequest = opts.onTextRequest;
    this.now = opts.now ?? (() => Date.now());
    this.nextId = opts.idgen ?? (() => `${this.author}-${this.now()}-${this.idCounter++}`);

    if (opts.interactive !== false) {
      this.canvas.addEventListener("pointerdown", this.onDown as EventListener);
      this.canvas.addEventListener("pointermove", this.onMove as EventListener);
      this.canvas.addEventListener("pointerup", this.onUp as EventListener);
      this.canvas.addEventListener("pointercancel", this.onUp as EventListener);
      this.canvas.addEventListener("pointerleave", this.onUp as EventListener);
    }
  }

  // ── Public API ───────────────────────────────────────────────────────────

  /** A snapshot copy of the current visible op-log. */
  get ops(): DrawOp[] {
    return this.items.map(cloneItem);
  }

  /** The currently-selected tool. */
  get currentTool(): Tool {
    return this.tool;
  }

  setTool(tool: Tool): void {
    this.tool = tool;
  }
  setColor(color: string): void {
    this.color = color;
  }
  setStrokeWidth(width: number): void {
    this.strokeWidth = width;
  }

  /** Apply an op received from a peer / the op-log. Does NOT re-emit it. */
  applyRemote(op: Op): void {
    if (this.destroyed) return;
    this.apply(op);
    this.redraw();
  }

  /**
   * Add a finalized text or sticky op at a point (called by the host after collecting
   * text inline — see {@link EngineOptions.onTextRequest}). Empty text is ignored.
   */
  addText(at: Point, text: string, kind: "text" | "sticky"): void {
    if (this.destroyed || !text.trim()) return;
    const base = { id: this.nextId(), author: this.author, ts: this.now() };
    const op: DrawOp =
      kind === "sticky"
        ? { op: "sticky", ...base, color: this.color, at, text }
        : { op: "text", ...base, color: this.color, at, text, size: DEFAULT_TEXT_SIZE };
    this.commitLocal(op);
  }

  /** Remove an op by id (undo). Emits an erase op so peers converge. */
  undo(opId: string): void {
    if (this.destroyed) return;
    if (!this.items.some((s) => s.id === opId)) return;
    const op = this.erase([opId]);
    this.redraw();
    this.emit(op);
  }

  /** Undo my most-recent item (pushes it onto the redo stack). */
  undoLastMine(): void {
    if (this.destroyed) return;
    const mine = this.items.filter((o) => o.author === this.author);
    const last = mine[mine.length - 1];
    if (!last) return;
    this.redoStack.push(cloneItem(last));
    const op = this.erase([last.id]);
    this.redraw();
    this.emit(op);
  }

  /** Redo my most-recently-undone item. */
  redoLastMine(): void {
    if (this.destroyed) return;
    const op = this.redoStack.pop();
    if (!op) return;
    this.apply(op);
    this.redraw();
    this.emit(op);
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /** Erase the topmost item under a point. Returns the erased id (or null); emits erase. */
  eraseAt(point: Point): string | null {
    if (this.destroyed) return null;
    const id = this.hitTest(point);
    if (!id) return null;
    const op = this.erase([id]);
    this.redraw();
    this.emit(op);
    return id;
  }

  /** Clear the whole board. Emits a clear op. */
  clear(): void {
    if (this.destroyed) return;
    const op: ClearOp = { op: "clear", id: this.nextId(), author: this.author, ts: this.now() };
    this.apply(op);
    this.redraw();
    this.emit(op);
  }

  /** Topmost item hit by `point` (within tolerance / inside its box). */
  hitTest(point: Point): string | null {
    for (let i = this.items.length - 1; i >= 0; i--) {
      if (itemHit(this.items[i], point, HIT_TOLERANCE)) return this.items[i].id;
    }
    return null;
  }

  /** Repaint the whole op-log (call after a canvas backing-size change). */
  refresh(): void {
    if (!this.destroyed) this.redraw();
  }

  /** Detach listeners and drop references — teardown discipline (WB-021). */
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.active = null;
    if (this.fadeTimer) clearTimeout(this.fadeTimer);
    this.fadeTimer = null;
    this.addedAt.clear();
    this.canvas.removeEventListener("pointerdown", this.onDown as EventListener);
    this.canvas.removeEventListener("pointermove", this.onMove as EventListener);
    this.canvas.removeEventListener("pointerup", this.onUp as EventListener);
    this.canvas.removeEventListener("pointercancel", this.onUp as EventListener);
    this.canvas.removeEventListener("pointerleave", this.onUp as EventListener);
  }

  // ── Op-log mutation (shared by local + remote) ───────────────────────────

  private apply(op: Op): void {
    if (isDrawOp(op)) {
      this.items.push(op);
      if (this.fadeMs > 0) {
        this.addedAt.set(op.id, this.now());
        this.scheduleFade();
      }
      return;
    }
    if (op.op === "erase") {
      const drop = new Set(op.targets);
      this.items = this.items.filter((s) => !drop.has(s.id));
      for (const id of op.targets) this.addedAt.delete(id);
      return;
    }
    // clear
    this.items = [];
    this.addedAt.clear();
  }

  // commitLocal applies a locally-authored draw op, clears the redo stack, repaints, emits.
  private commitLocal(op: DrawOp): void {
    this.redoStack = [];
    this.apply(op);
    this.redraw();
    this.emit(op);
  }

  private scheduleFade(): void {
    if (this.fadeMs <= 0 || this.fadeTimer || this.destroyed) return;
    this.fadeTimer = setTimeout(() => {
      this.fadeTimer = null;
      if (this.destroyed) return;
      const now = this.now();
      const before = this.items.length;
      this.items = this.items.filter((s) => {
        const age = now - (this.addedAt.get(s.id) ?? now);
        if (age >= this.fadeMs + FADE_DURATION_MS) {
          this.addedAt.delete(s.id);
          return false;
        }
        return true;
      });
      if (this.items.length !== before || this.items.length > 0) this.redraw();
      if (this.items.length > 0) this.scheduleFade();
    }, 40);
  }

  private erase(targets: string[]): EraseOp {
    const op: EraseOp = { op: "erase", id: this.nextId(), author: this.author, ts: this.now(), targets };
    this.apply(op);
    return op;
  }

  private emit(op: Op): void {
    this.onLocalOp?.(op);
  }

  // ── Pointer input → ops ───────────────────────────────────────────────────

  private handlePointerDown(e: PointerLike): void {
    if (this.destroyed) return;
    if (e.button !== undefined && e.button !== 0) return;
    const p = this.eventPoint(e);
    switch (this.tool) {
      case "text":
      case "sticky":
        this.onTextRequest?.(p, this.tool); // host collects text inline
        return;
      case "eraser":
        this.active = { kind: "erase" };
        this.eraseAt(p);
        return;
      case "line":
      case "rect":
      case "ellipse":
      case "arrow":
        this.active = { kind: "shape", shape: this.tool, from: p, to: p };
        return;
      default:
        this.active = { kind: "stroke", points: [p], lastTs: this.now(), highlight: this.tool === "highlighter" };
    }
  }

  private handlePointerMove(e: PointerLike): void {
    if (this.destroyed || !this.active) return;
    const p = this.eventPoint(e);
    if (this.active.kind === "erase") {
      this.eraseAt(p);
      return;
    }
    if (this.active.kind === "shape") {
      this.active.to = p;
      this.redraw();
      this.paintShape(this.active.shape, this.active.from, this.active.to, this.color);
      return;
    }
    const last = this.active.points[this.active.points.length - 1];
    const t = this.now();
    if (t - this.active.lastTs < MIN_POINT_INTERVAL_MS && dist(last, p) < MIN_POINT_DISTANCE) return;
    this.drawSegment(last, p, this.active.highlight);
    this.active.points.push(p);
    this.active.lastTs = t;
  }

  private handlePointerUp(e: PointerLike): void {
    if (this.destroyed || !this.active) return;
    const g = this.active;
    this.active = null;
    if (g.kind === "erase") return;
    if (g.kind === "shape") {
      const to = e.clientX !== undefined ? this.eventPoint(e) : g.to;
      if (dist(g.from, to) < MIN_POINT_DISTANCE) {
        this.redraw(); // a click, not a drag — discard the degenerate shape
        return;
      }
      this.commitLocal({ op: "shape", id: this.nextId(), author: this.author, ts: this.now(), kind: g.shape, color: this.color, from: g.from, to });
      return;
    }
    // stroke
    if (e.clientX !== undefined) {
      const p = this.eventPoint(e);
      const last = g.points[g.points.length - 1];
      if (dist(last, p) >= MIN_POINT_DISTANCE) {
        this.drawSegment(last, p, g.highlight);
        g.points.push(p);
      }
    }
    this.commitLocal({
      op: "stroke",
      id: this.nextId(),
      author: this.author,
      ts: this.now(),
      color: this.color,
      points: g.points,
      ...(g.highlight ? { highlight: true } : {}),
    });
  }

  private eventPoint(e: PointerLike): Point {
    const rect = this.canvas.getBoundingClientRect();
    return pixelToNorm((e.clientX ?? 0) - rect.left, (e.clientY ?? 0) - rect.top, { width: rect.width, height: rect.height });
  }

  // ── Rendering ─────────────────────────────────────────────────────────────

  private backingSize(): Size {
    return { width: this.canvas.width, height: this.canvas.height };
  }
  private scaledLineWidth(): number {
    return this.strokeWidth * (this.canvas.width / this.referenceSize.width);
  }

  private redraw(): void {
    const size = this.backingSize();
    this.ctx.clearRect(0, 0, size.width, size.height);
    const now = this.now();
    for (const item of this.items) {
      const fade = this.fadeMs > 0 ? fadeOpacity(now - (this.addedAt.get(item.id) ?? now), this.fadeMs) : 1;
      if (fade <= 0) continue;
      const isHi = item.op === "stroke" && item.highlight;
      this.ctx.globalAlpha = fade * (isHi ? HIGHLIGHT_ALPHA : 1);
      this.paintItem(item);
    }
    this.ctx.globalAlpha = 1;
  }

  private paintItem(item: DrawOp): void {
    switch (item.op) {
      case "stroke":
        this.paintStroke(item);
        break;
      case "shape":
        this.paintShape(item.kind, item.from, item.to, item.color);
        break;
      case "text":
        this.paintText(item);
        break;
      case "sticky":
        this.paintSticky(item);
        break;
    }
  }

  private paintStroke(stroke: StrokeOp): void {
    const size = this.backingSize();
    const ctx = this.ctx;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = this.scaledLineWidth() * (stroke.highlight ? HIGHLIGHT_WIDTH_MULT : 1);
    ctx.strokeStyle = stroke.color;
    if (stroke.points.length === 1) {
      const { px, py } = normToPixel(stroke.points[0], size);
      ctx.beginPath();
      ctx.fillStyle = stroke.color;
      ctx.arc(px, py, ctx.lineWidth / 2, 0, Math.PI * 2);
      ctx.fill();
      return;
    }
    ctx.beginPath();
    stroke.points.forEach((pt, i) => {
      const { px, py } = normToPixel(pt, size);
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    });
    ctx.stroke();
  }

  private paintShape(kind: ShapeOp["kind"], from: Point, to: Point, color: string): void {
    const size = this.backingSize();
    const ctx = this.ctx;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = this.scaledLineWidth();
    ctx.strokeStyle = color;
    const a = normToPixel(from, size);
    const b = normToPixel(to, size);
    if (kind === "line" || kind === "arrow") {
      ctx.beginPath();
      ctx.moveTo(a.px, a.py);
      ctx.lineTo(b.px, b.py);
      ctx.stroke();
      if (kind === "arrow") this.paintArrowHead(a, b);
      return;
    }
    const x = Math.min(a.px, b.px);
    const y = Math.min(a.py, b.py);
    const w = Math.abs(b.px - a.px);
    const h = Math.abs(b.py - a.py);
    if (kind === "rect") {
      ctx.strokeRect(x, y, w, h);
      return;
    }
    // ellipse
    ctx.beginPath();
    ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
    ctx.stroke();
  }

  private paintArrowHead(a: { px: number; py: number }, b: { px: number; py: number }): void {
    const ctx = this.ctx;
    const angle = Math.atan2(b.py - a.py, b.px - a.px);
    const len = Math.max(8, this.scaledLineWidth() * 3);
    const spread = Math.PI / 7;
    ctx.beginPath();
    ctx.moveTo(b.px, b.py);
    ctx.lineTo(b.px - len * Math.cos(angle - spread), b.py - len * Math.sin(angle - spread));
    ctx.moveTo(b.px, b.py);
    ctx.lineTo(b.px - len * Math.cos(angle + spread), b.py - len * Math.sin(angle + spread));
    ctx.stroke();
  }

  private paintText(item: TextOp): void {
    const size = this.backingSize();
    const ctx = this.ctx;
    const px = item.at.x * size.width;
    const py = item.at.y * size.height;
    const fontPx = (item.size ?? DEFAULT_TEXT_SIZE) * (this.canvas.width / this.referenceSize.width);
    ctx.font = `${fontPx}px sans-serif`;
    ctx.textBaseline = "top";
    ctx.fillStyle = item.color;
    item.text.split("\n").forEach((line, i) => ctx.fillText(line, px, py + i * fontPx * 1.2));
  }

  private paintSticky(item: StickyOp): void {
    const size = this.backingSize();
    const ctx = this.ctx;
    const x = item.at.x * size.width;
    const y = item.at.y * size.height;
    const w = STICKY_W * size.width;
    const h = STICKY_H * size.height;
    ctx.fillStyle = item.color;
    ctx.fillRect(x, y, w, h);
    // Dark text on the note for legibility (notes use the light-ish palette colors).
    const fontPx = 22 * (this.canvas.width / this.referenceSize.width);
    ctx.font = `${fontPx}px sans-serif`;
    ctx.textBaseline = "top";
    ctx.fillStyle = "#11202a";
    wrapText(item.text, 18).forEach((line, i) => ctx.fillText(line, x + 8, y + 8 + i * fontPx * 1.2));
  }

  private drawSegment(a: Point, b: Point, highlight: boolean): void {
    const size = this.backingSize();
    const ctx = this.ctx;
    ctx.globalAlpha = highlight ? HIGHLIGHT_ALPHA : 1;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = this.scaledLineWidth() * (highlight ? HIGHLIGHT_WIDTH_MULT : 1);
    ctx.strokeStyle = this.color;
    const from = normToPixel(a, size);
    const to = normToPixel(b, size);
    ctx.beginPath();
    ctx.moveTo(from.px, from.py);
    ctx.lineTo(to.px, to.py);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }
}

// ── Geometry helpers ─────────────────────────────────────────────────────────

interface PointerLike {
  clientX?: number;
  clientY?: number;
  button?: number;
}

function cloneItem(item: DrawOp): DrawOp {
  return item.op === "stroke" ? { ...item, points: item.points.slice() } : { ...item };
}

function dist(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

// A crude fixed-width word wrap (no canvas measurement needed — stub-safe).
function wrapText(text: string, maxChars: number): string[] {
  const out: string[] = [];
  for (const paragraph of text.split("\n")) {
    let line = "";
    for (const word of paragraph.split(/\s+/)) {
      if (line && (line + " " + word).length > maxChars) {
        out.push(line);
        line = word;
      } else {
        line = line ? `${line} ${word}` : word;
      }
    }
    out.push(line);
  }
  return out.slice(0, 5); // cap; the sticky is small
}

/** True if `point` hits `item` within `tol` (near a stroke/line, or inside a box). */
function itemHit(item: DrawOp, point: Point, tol: number): boolean {
  switch (item.op) {
    case "stroke": {
      const pts = item.points;
      if (pts.length === 1) return dist(pts[0], point) <= tol;
      for (let i = 1; i < pts.length; i++) {
        if (pointSegmentDistance(point, pts[i - 1], pts[i]) <= tol) return true;
      }
      return false;
    }
    case "shape":
      if (item.kind === "line" || item.kind === "arrow") return pointSegmentDistance(point, item.from, item.to) <= tol;
      return inBox(point, item.from, item.to, tol);
    case "text":
      return inBox(point, item.at, { x: item.at.x + 0.12, y: item.at.y + 0.04 }, tol);
    case "sticky":
      return inBox(point, item.at, { x: item.at.x + STICKY_W, y: item.at.y + STICKY_H }, tol);
  }
}

function inBox(p: Point, a: Point, b: Point, tol: number): boolean {
  const x0 = Math.min(a.x, b.x) - tol;
  const x1 = Math.max(a.x, b.x) + tol;
  const y0 = Math.min(a.y, b.y) - tol;
  const y1 = Math.max(a.y, b.y) + tol;
  return p.x >= x0 && p.x <= x1 && p.y >= y0 && p.y <= y1;
}

function pointSegmentDistance(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return dist(p, a);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  t = clampUnit(t);
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}
